import {NEVER, Observable} from 'rxjs';
import {concatMap, map} from 'rxjs/operators';

import {Injectable} from '@angular/core';

import * as APIM from '@common/utils/dist/models/api-messages.js';
import * as PL2 from '@common/utils/dist/models/models-pl2.js';
import {EntryUtilsPL2 as EU} from '@common/utils/dist/utils/entry-utils-pl2.js';
import {KeyUtilsPL2 as KU} from '@common/utils/dist/utils/key-utils-pl2.js';

import {EntryStore} from '@stores/entry.store';
import {EntryStoreUtils as ESU} from '@utils/entry-store-utils';
import {
  WebsocketMessageProcessor,
  WSMessageProcessorInput,
  WSMessageProcessorOutput,
} from './websocket-message-processor';
import {WSMessageReceiverService} from '@services/ws-message-receiver.service';
import {UserStore} from '@stores/user.store';
import {ReceiveEventSuccessAction} from '@app/actions/receive-event-success.action';
import {ReceivePartialChangesTransformer} from '@stores/entry-store-transformers/receive-partial-changes-transformer';

@Injectable()
export class EntryMessageProcessor implements WebsocketMessageProcessor {
  private _acceptedIntents = [
    APIM.EventIntent.ClassesSynchronized,
    APIM.EventIntent.ExistingDAUserMigrated,
    APIM.EventIntent.EntryCreated,
    APIM.EventIntent.EntryUpdated,
    APIM.EventIntent.EntryDeleted,
    APIM.EventIntent.EntryCopied,
    APIM.EventIntent.AssignmentCreated,
    APIM.EventIntent.AssignmentUpdated,
    APIM.EventIntent.AssignmentDeleted,
    APIM.EventIntent.LabReportUserCreated,
    APIM.EventIntent.LabReportUserUpdated,
    APIM.EventIntent.LicenseCreated,
    APIM.EventIntent.VToSCEntryCopied,
    APIM.EventIntent.LMSVToSCEntryCopied,
    APIM.EventIntent.InviteAccepted,
    APIM.EventIntent.LMSInviteAccepted,
    APIM.EventIntent.TrialCreated,
    APIM.EventIntent.LrEnbToggled,
  ];

  constructor(
    private entryStore: EntryStore,
    private wsMessageReceiverService: WSMessageReceiverService,
    private userStore: UserStore,
    private receiveEventSuccessAction: ReceiveEventSuccessAction,
    private receivePartialChangesTransformer: ReceivePartialChangesTransformer,
  ) {}

  processMessage(
    params: WSMessageProcessorInput,
  ): Observable<WSMessageProcessorOutput> {
    const message = params.msg;
    if (this._acceptedIntents.includes(message.int)) {
      return this.receiveEventSuccessAction.execute(message.id).pipe(
        concatMap(() =>
          this.wsMessageReceiverService
            .receive<APIM.NotifyRequest>(message)
            .pipe(
              map((request: APIM.NotifyRequest) => {
                if (
                  !Array.isArray(request.c) ||
                  request.c.length === 0 ||
                  !EU.isEntryLike(request.c[0])
                ) {
                  params.wsAPI.result(message, request);
                  return;
                }
                const formattedEntries = (<PL2.AnyEntry[]>request.c)
                  .filter((e) => ESU.isKnownEntry(e, this.entryStore.state()))
                  .map((e) => ESU.formatEntry(e))
                  .sort((a, b) =>
                    KU.stringFromKey(a as PL2.EntryId) >
                    KU.stringFromKey(b as PL2.EntryId)
                      ? 1
                      : -1,
                  );
                if (
                  message.int === APIM.EventIntent.LrEnbToggled ||
                  formattedEntries.findIndex(ESU.isPermissionEntry) > -1
                ) {
                  this.userStore.refreshAttributes().subscribe();
                }
                this.entryStore.modifyStateWithTransformer(
                  formattedEntries as PL2.AnyPartialEntryWithId[],
                  this.receivePartialChangesTransformer,
                );
                params.wsAPI.result(message, request);
              }),
              map(() => true),
            ),
        ),
      );
    }
    return NEVER;
  }
}
